// frontend/src/pages/InstallerOrdersPage.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Package, Eye, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { PageSEO } from '../components/SEO/PageSEO';
import config from '../config';

export const InstallerOrdersPage = () => {
  const { user } = useAuthStore();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, [page]);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(config.getApiUrl('/orders/'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          window.location.href = '/login';
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Installer orders API response:', data);
      
      const ordersList = data.results || [];
      setOrders(ordersList);
      setTotalPages(Math.ceil(data.count / 10) || 1);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('خطا در دریافت سفارشات');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    const statusText = {
      pending: 'در انتظار پرداخت',
      paid: 'پرداخت شده',
      processing: 'در حال پردازش',
      shipped: 'ارسال شده',
      delivered: 'تحویل شده',
      cancelled: 'لغو شده',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${statusMap[status] || 'bg-gray-100'}`}>
        {statusText[status] || status}
      </span>
    );
  };

  const formatOrderId = (id) => {
    if (!id) return '---';
    const numericId = id.replace(/-/g, '').slice(0, 8).toUpperCase();
    return numericId;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button onClick={fetchOrders} className="bg-blue-600 text-white px-4 py-2 rounded-lg">
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h1 className="text-2xl font-bold text-gray-900">سفارشات من</h1>
            </div>
            <div className="p-12 text-center">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">شما هیچ سفارشی ندارید</p>
              <Link to="/products" className="mt-4 inline-block bg-blue-600 text-white px-6 py-2 rounded-lg">
                شروع خرید
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageSEO title="سفارشات من | پنل نصاب" noindex={true} />
      
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h1 className="text-2xl font-bold text-gray-900">سفارشات من</h1>
              <p className="text-sm text-gray-500 mt-1">تعداد کل: {orders.length} سفارش</p>
            </div>

            <div className="divide-y divide-gray-200">
              {orders.map((order) => (
                <div key={order.id} className="p-6 hover:bg-gray-50 transition">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className="font-bold text-gray-900">#{formatOrderId(order.id)}</span>
                        {getStatusBadge(order.status)}
                      </div>
                      <div className="text-sm text-gray-500">
                        تاریخ: {new Date(order.created_at).toLocaleDateString('fa-IR')}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        مبلغ: {order.final_amount?.toLocaleString()} تومان
                      </div>
                      <div className="text-sm text-gray-500">
                        تعداد اقلام: {order.items?.length || 0}
                      </div>
                    </div>
                    <div className="text-right">
                      <Link
                        to={`/orders/${order.id}`}
                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <Eye className="w-4 h-4" />
                        مشاهده جزئیات
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="p-4 border-t border-gray-200 flex justify-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <span className="py-2 px-4">صفحه {page} از {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};